import React, { ReactNode, createContext, useContext, useState } from "react";

import { Bounds, Dimensions, Point, Image, EditingMode } from "../types";

// Internal state for cropper
type Context = {
  imageData: Image;
  setImageData: (data: Image) => void;
  imageScaleFactor: number;
  setImageScaleFactor: (scale: number) => void;
  imageBounds: Bounds;
  setImageBounds: (bounds: Bounds) => void;
  isReady: boolean;
  setIsReady: (ready: boolean) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  accumulatedPan: Point;
  setAccumulatedPan: (pan: Point) => void;
  cropSize: Dimensions;
  setCropSize: (size: Dimensions) => void;
  initialEditingMode: EditingMode; // a copy of the initial mode to track if we must crop
  editingMode: EditingMode;
  setEditingMode: (mode: EditingMode) => void;
  isEdit: boolean;
  setIsEdit: (edit: boolean) => void;
};

export const CropperContext = createContext<Context | null>(null);

export default function useCropperContext() {
  const ctx = useContext(CropperContext);

  if (!ctx) throw new Error("Missing CropperContextProvider");

  return ctx;
}

const initialDimensions: Dimensions = { width: 0, height: 0 };
const initialPoint: Point = { x: 0, y: 0 };

type Props = {
  initialEditingMode: EditingMode;
  children: ReactNode;
};

export function CropperContextProvider({
  initialEditingMode,
  children,
}: Props) {
  const [imageData, setImageData] = useState<Image>({
    uri: "",
    ...initialDimensions,
  });
  const [imageScaleFactor, setImageScaleFactor] = useState(1);
  const [imageBounds, setImageBounds] = useState<Bounds>({
    ...initialDimensions,
    ...initialPoint,
  });
  const [isReady, setIsReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [accumulatedPan, setAccumulatedPan] = useState<Point>(initialPoint);
  const [cropSize, setCropSize] = useState<Dimensions>(initialDimensions);

  const [editingMode, setEditingMode] =
    useState<EditingMode>(initialEditingMode);
  const [isEdit, setIsEdit] = useState(false);

  return (
    <CropperContext.Provider
      value={{
        imageData,
        setImageData,
        imageScaleFactor,
        setImageScaleFactor,
        imageBounds,
        setImageBounds,
        isReady,
        setIsReady,
        isProcessing,
        setIsProcessing,
        accumulatedPan,
        setAccumulatedPan,
        cropSize,
        setCropSize,
        editingMode,
        setEditingMode,
        isEdit,
        setIsEdit,
        initialEditingMode,
      }}
    >
      {children}
    </CropperContext.Provider>
  );
}
